// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import {
  coerceBoolean,
  PaginationInfoSchema,
  ResponseMessageSchema,
  StatusSchema,
} from "./utilitySchemas";
import { UserResponseSchema } from "./userSchemas";

/**
 * DOC: ENTITY SCHEMAS AND TYPES
 */

export const LeadSourceSchema = z.enum([ 
  "GUTACHTEN_ORG",
  "PERSONAL_RECOMMENDATION",
  "DERGUTACHTER_LARS",
  "SACHNET",
  "IMMOCATION",
  "SOCIAL_MEDIA",
  "INTERNET_SEARCH",
  "OTHER"]);

export type LeadSourceEnum = z.infer<typeof LeadSourceSchema>;

export const EntityPrioritySchema = z.enum([
  "CRITICAL",
  "HIGH",
  "STANDARD",
  "LOW",
  "NOT_SET",
]);
export type EntityPriority = z.infer<typeof EntityPrioritySchema>;

export const EntityUserMetadataSchema = z.object({
  active: z.number(),
  inactive: z.number(),
  total: z.number(),
});

export const EntitySchema = z.object({
  id: z.number().int().positive(),
  evalionId: z.string(),
  name: z.string(),
  streetName: z.string(),
  streetNumber: z.string(),
  city: z.string(),
  postalCode: z.string(),
  country: z.string(),
  industry: z.string(),
  type: z.string(),
  active: z.boolean(),
  priority: EntityPrioritySchema.default("NOT_SET"),
  createdAt: z.date(),
  updatedAt: z.date(),
  leadSource: LeadSourceSchema,
  referralSource:z.object({
    id:z.string(),
    name:z.string(),
    type: z.string()
  }).optional().nullable(),
  externalReferralSource:z.object({
    id:z.string(),
    name:z.string(),
    type: z.string()
  }).optional().nullable(),
  appraiserId: z.string().optional(),
  paymentContactsCount:z.number(),
  stripePriceList:z.object(
    {
      id: z.string(),
      name: z.string(),
      description:z.string()
    }
  ),
  extraServicesPriceListId: z.string().optional(),
  extraServicesPriceList: z.object(
    {
      id: z.string(),
      name: z.string(),
      description:z.string()
    }
  ).optional(),
  users: z.array(UserResponseSchema.omit({
    entity: true,
    podAdminId: true,
  })),
  _count: z.object({
    users: z.number(),
  }).optional(),
  metadata: z.object({
    users: z.object({
      mainUsers: EntityUserMetadataSchema,
      adminUsers: EntityUserMetadataSchema,
      subUsers: EntityUserMetadataSchema,
      activeNonAdminUsers: z.number(),
    }).optional()
  }).optional(),
  accountOwnerId:z.number().nullable(),
  accountOwner:UserResponseSchema.omit({
    entity: true,
    podAdminId: true,
  }).nullable(),
});
export type Entity = z.infer<typeof EntitySchema>;

export const EntitySortByEnumSchema = z
.enum([
  "id",
  "name",
  "streetName",
  "streetNumber",
  "city",
  "country",
  "postalCode",
  "industry",
  "type",
  "createdAt",
  "updatedAt",
  "firstName",
  "lastName",
  "email",
]);
export type EntitySortByEnum = z.infer<typeof EntitySortByEnumSchema>;

export const EntityQuerySchema = z.object({
  limit: z.coerce.number().int().positive().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
  sortBy: EntitySortByEnumSchema
    .optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  filter: z
    .object({
      active: z
        .union([
          z.boolean(),
          z.enum(["true", "false"]).transform((val) => val === "true"),
        ])
        .optional(),
        invited:z
        .union([
          z.boolean(),
          z.enum(["true", "false"]).transform((val) => val === "true"),
        ])
        .optional(),
        search: z.string().optional(),
        newAccountRequests: coerceBoolean().optional(),
        appraiser: z.string().optional(),
    })
    .optional(),
});
export type EntityQuery = z.infer<typeof EntityQuerySchema>;

export const EntityFilterQuerySchema = EntityQuerySchema.shape.filter;
export type EntityFilterQuery = z.infer<typeof EntityFilterQuerySchema>;

export const CreateEntitySchema = z.object({
  name: z.string().min(2),
  streetName: z.string().min(1),
  streetNumber: z.string().min(1),
  city: z.string().min(2),
  country: z.string().min(3),
  postalCode: z.string().min(2),
  industry: z.string().min(2),
  type: z.string().min(2),
  leadSource: LeadSourceSchema.optional(),   // REMOVE ONCE REFERRAL SOURCE IS IMPLEMENTED
  stripePriceListId: z.string().optional(),
  appraiserId: z.string().optional(),
  referralSourceId: z.string().optional(),
  accountOwnerId: z.number().nullable().optional(),
  hubspotCompanyId: z.string().optional(),
});
export type CreateEntity = z.infer<typeof CreateEntitySchema>;

export const UpdateEntitySchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters.").optional(),
  streetName: z.string().min(1, "Street Name must be at least 1 character.").optional(),
  streetNumber: z.string().min(1, "Street Number must be at least 1 character.").optional(),
  city: z.string().min(2, "City must be at least 2 characters.").optional(),
  country: z.string().min(3, "Country must be at least 3 characters.").optional(),
  postalCode: z.string().min(2, "Postal Code must be at least 2 characters.").optional(),
  industry: z.string().min(2, "Industry must be at least 2 characters.").optional(),
  type: z.string().min(2, "Type must be at least 2 characters.").optional(),
  active: z.boolean().optional(),
  leadSource: LeadSourceSchema.optional(),
  stripePriceListId: z.string().optional(),
  appraiserId: z.string().optional(),
  referralSourceId: z.string().nullable().optional(),
  externalReferralSourceId: z.string().nullable().optional(),
  accountOwnerId: z.number().nullable().optional(),
  extraServicesPriceListId: z.string().optional(),
  priority: EntityPrioritySchema.optional(),
});
export type UpdateEntity = z.infer<typeof UpdateEntitySchema>;

const EntityDeleteActionTypeSchema = z.enum(["delete", "rejectAccountRequest"]);
export type EntityDeleteActionType = z.infer<typeof EntityDeleteActionTypeSchema>;

export const DeleteEntitySchema = z.object({
  actionType: EntityDeleteActionTypeSchema.optional(),
});

export type DeleteEntity = z.infer<typeof DeleteEntitySchema>;

/**
 * DOC: ENTITY RESPONSE SCHEMAS AND TYPES
 */
export const EntityResponseSchema = EntitySchema.omit({
  createdAt: true,
  updatedAt: true,
}).extend({
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EntityResponse = z.infer<typeof EntityResponseSchema>;

export const EntityCreateResponseSchema = EntityResponseSchema;
export type EntityCreateResponse = z.infer<typeof EntityCreateResponseSchema>;

export const EntityUpdateResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: EntityResponseSchema,
});
export type EntityUpdateResponse = z.infer<typeof EntityUpdateResponseSchema>;

export const GetAllEntitiesResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    entities: z.array(EntityResponseSchema),
    paginationInfo: PaginationInfoSchema,
  }),
});

export type GetAllEntitiesResponse = z.infer<
  typeof GetAllEntitiesResponseSchema
>;

export const GetEntityResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: EntityResponseSchema,
});

export const EntityDeleteResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.null(),
});
export type EntityDeleteResponse = z.infer<typeof EntityDeleteResponseSchema>;

export const EntityBillingManagementSchema = z.object({
  priceListId: z.number(),
})
export type EntityBillingManagement = z.infer<typeof EntityBillingManagementSchema>;

export const CheckEntityFieldUniquenessSchema = z.object({
  field: z.enum(['name', "referralSourceName"]),
  value: z.string().min(1, "Value cannot be empty")
});

export const CheckEntityFieldUniquenessResponseSchema = z.object({
  status: StatusSchema,
  data: z.object({
    exists: z.boolean()
  }),
});

export const EntityReferralSourceTypeEnumSchema = z.enum([
  "SIGN_UP",
  "INVITATION",
  "EXTERNAL_PARTNER",
]);

export type EntityReferralSourceTypeEnum = z.infer<typeof EntityReferralSourceTypeEnumSchema>;

export const CheckEntityReferralFieldUniquenessSchema = z.object({
  field: z.enum(['name']),
  type: EntityReferralSourceTypeEnumSchema,
  value: z.string().min(1, "Value cannot be empty")
});

export const EntityReferralSourceQuerySchema = z.object({
  limit: z.coerce.number().int().positive().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
  sortBy: z.enum(["id", "name", "type", "sortOrder", "entity"])
    .optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  filter: z
    .object({
      type: EntityReferralSourceTypeEnumSchema.optional(),
      appraiserId: z.string().optional(),
    })
    .optional(),
});

export type EntityReferralSourceQuery = z.infer<typeof EntityReferralSourceQuerySchema>;

export const EntityReferralSourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: EntityReferralSourceTypeEnumSchema,
  sortOrder: z.number().int().optional(),
  appraisers:z.array(
    z.object(
      {
        id: z.string(),
        name: z.string(),
      }
    )
  ),
  externalReferralEntities:z.array(
    z.object(
      {
        id: z.number(),
        name: z.string(),
      }
    )
  ).optional(),
  _count: z.object({
    entities: z.number(),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type EntityReferralSource = z.infer<typeof EntityReferralSourceSchema>;

export const GetEntityReferralSourcesResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.array(EntityReferralSourceSchema)
});
export type GetEntityReferralSourcesResponse = z.infer<typeof GetEntityReferralSourcesResponseSchema>;

export const EntityReferralSourceResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: EntityReferralSourceSchema
});
export type EntityReferralSourceResponse = z.infer<typeof EntityReferralSourceResponseSchema>;

export const CreateEntityReferralSourceSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters."),
  type: EntityReferralSourceTypeEnumSchema,
  appraiserIds: z.array(z.string()).optional(),
});
export type CreateEntityReferralSource = z.infer<typeof CreateEntityReferralSourceSchema>;

export const UpdateEntityReferralSourceSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters.").optional(),
  sortOrder: z.number().int().optional(),
  appraiserIds: z.array(z.string()).optional(),
  externalEntityIds: z.array(z.number().int().positive()).optional(),
});
export type UpdateEntityReferralSource = z.infer<typeof UpdateEntityReferralSourceSchema>;
